import { RouteProp, useNavigation } from '@react-navigation/native';
import { useCardAnimation } from '@react-navigation/stack';
import { Button, Icon, Text, useTheme } from '@ui-kitten/components';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useState } from 'react';
import { Animated, Pressable, View } from 'react-native';

import useResponsiveStyleSheet, {
  createResponsiveStyle,
} from '../../hooks/useResponsiveStyleSheet';
import { AppStackNavigation, AppStackParamList } from '../../navigation';
import { useStore } from '../../stores';

type Props = { route: RouteProp<AppStackParamList, 'Mandatory Training'> };

const MandatoryTrainingModal: React.FC<Props> = (props) => {
  const styles = useResponsiveStyleSheet(themedStyles);
  const theme = useTheme();
  const navigation = useNavigation<AppStackNavigation>();
  const { current } = useCardAnimation();

  const store = useStore();
  const { authStore, userStore } = store;
  const [loading, setLoading] = useState(false);

  const user = authStore.user!;

  const onTrainingCompleted = useCallback(
    async (completed: boolean) => {
      if (loading) {
        return;
      }

      setLoading(true);

      const result = await userStore.setMandatoryTraining(
        Number(user.id),
        completed,
      );

      if (result.ok) {
        navigation.navigate('Main', {
          screen: 'Dashboard',
        });
      }

      setLoading(false);
    },
    [loading, user.id],
  );

  return (
    <View style={styles.container}>
      <Pressable style={styles.pressable} />
      <Animated.View
        style={[
          styles.card,
          {
            transform: [
              {
                scale: current.progress.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0.9, 1],
                  extrapolate: 'clamp',
                }),
              },
            ],
          },
        ]}
      >
        <View style={styles.textBox}>
          <Text style={[styles.text, { fontSize: 24 }]}>
            Mandatory Training{'\n\n'}
          </Text>
          <Text style={[styles.text, { color: theme['text-grey'] }]}>
            Have you completed the mandatory training?
          </Text>
        </View>
        <View style={styles.buttonsContainer}>
          <Button
            status="primary"
            size="large"
            appearance="filled"
            style={styles.button}
            disabled={loading}
            accessoryLeft={() => (
              <Icon
                name="checkmark-outline"
                width={24}
                height={24}
                fill={theme['text-white']}
              />
            )}
            onPress={() => onTrainingCompleted(true)}
          >
            Yes
          </Button>
          <Button
            status="basic"
            size="large"
            appearance="outline"
            style={styles.button}
            disabled={loading}
            accessoryLeft={() => (
              <Icon
                name="close-outline"
                width={24}
                height={24}
                fill={theme['text-primary']}
              />
            )}
            onPress={() => onTrainingCompleted(false)}
          >
            No
          </Button>
        </View>
      </Animated.View>
    </View>
  );
};

const themedStyles = createResponsiveStyle({
  baseStyle: {
    container: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    pressable: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
    },
    card: {
      backgroundColor: '#FFFFFF',
      borderRadius: 4,
      paddingVertical: 30,
      paddingHorizontal: 30,
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: 540,
      height: 400,
    },
    textBox: {
      textAlign: 'center',
      marginBottom: 40,
    },
    text: {
      fontSize: 16,
      fontFamily: 'UniversBP_Light',
      marginTop: 8,
      marginBottom: 8,
    },
    buttonsContainer: {
      flexDirection: 'row',
      gap: 16,
      justifyContent: 'center',
      alignItems: 'center',
    },
    button: {
      width: 150,
    },
  },
  tabletStyle: {},
});

export default observer(MandatoryTrainingModal);
